import { useNavigate } from "react-router";

import FlashContext from "components/FlashMessages/FlashContext";
import Button, { ButtonProps } from "ds/components/Button";
import DropdownMenuItem from "ds/components/DropdownMenu/Item";
import SplitControlButton from "ds/components/SplitControl/Button";
import SplitControlDropdown from "ds/components/SplitControl/Dropdown";
import SplitControlWrapper from "ds/components/SplitControl/Wrapper";
import { SplitControlVariant } from "ds/components/SplitControl/context";
import Tooltip from "ds/components/Tooltip";
import useTypedContext from "hooks/useTypedContext";
import useTriggerStack from "shared/Stack/useTriggerStack";
import { ForceApplyMode, Stack } from "types/generated";
import useCanTriggerStack from "shared/Stack/access/useCanTriggerStack";

import { showStackRunForceApplyDrawer } from "./ForceApplyDrawer";
import { showTriggerWithCustomConfigDrawer } from "./TriggerWithCustomConfigDrawer";

type TriggerButtonProps = {
  stack: Stack;
  text: string;
  variant?: SplitControlVariant;
  size?: ButtonProps["size"];
  allowCustomConfig?: boolean;
};

const TriggerButton = ({
  stack,
  text,
  size = "medium",
  variant = "primary",
  allowCustomConfig = false,
}: TriggerButtonProps) => {
  const { reportSuccess } = useTypedContext(FlashContext);

  const navigate = useNavigate();

  const [triggerRun, { loading }] = useTriggerStack();

  const canTriggerFn = useCanTriggerStack();
  const canTrigger = canTriggerFn(stack);
  const canTriggerWithCustomConfig = canTriggerFn(stack, true);

  const onClick = (
    customRuntimeConfig: string | null = null,
    forceApply: ForceApplyMode | null = null
  ) =>
    triggerRun(
      {
        stack: stack.id,
        runtimeConfig: customRuntimeConfig ? { yaml: customRuntimeConfig } : null,
        forceApply,
      },
      (data) => {
        const id = data?.runTrigger.id;
        if (id) {
          reportSuccess({ message: `Run ${id} successfully triggered` });
          navigate(`/stack/${stack.id}/run/${id}`);
        }
      }
    );

  const openCustomTriggerConfigDrawer = () => {
    showTriggerWithCustomConfigDrawer({
      onTrigger: onClick,
    });
  };

  const openForceApplyDrawer = () => {
    showStackRunForceApplyDrawer({
      onTrigger: (mode: ForceApplyMode) => onClick(null, mode),
    });
  };

  // If both trigger options are disabled and have the same message, we don't want to show the dropdown
  // If they are both disabled then they have most likely been disabled by the same reason
  // This is to avoid showing an empty dropdown
  const isDropdownDisabled =
    loading ||
    (!canTrigger.result &&
      !canTriggerWithCustomConfig.result &&
      canTriggerWithCustomConfig.message === canTrigger.message);

  if (
    !canTrigger.result &&
    !canTrigger.message &&
    !canTriggerWithCustomConfig.result &&
    !canTriggerWithCustomConfig.message
  ) {
    return null;
  }

  return (
    <>
      {allowCustomConfig ? (
        <SplitControlWrapper size={size} variant={variant}>
          <SplitControlButton
            tooltip={canTrigger.message}
            disabled={loading || (!!canTrigger.message && !canTrigger.result)}
            loading={loading}
            id="stack-view--trigger-split-button"
            onPress={() => onClick()}
          >
            {text}
          </SplitControlButton>
          <SplitControlDropdown
            tooltip={canTrigger.message || "More trigger options"}
            disabled={isDropdownDisabled}
            placement="bottom right"
          >
            <DropdownMenuItem
              tooltip={canTrigger.message}
              isDisabled={loading || (!!canTrigger.message && !canTrigger.result)}
              isTooltipActive={!!canTrigger.message}
              tooltipPlacement="left"
              onAction={() => {
                onClick();
              }}
            >
              {text}
            </DropdownMenuItem>
            <DropdownMenuItem
              tooltip={canTrigger.message}
              isDisabled={loading || (!!canTrigger.message && !canTrigger.result)}
              isTooltipActive={!!canTrigger.message}
              tooltipPlacement="left"
              onAction={openForceApplyDrawer}
            >
              Trigger with force apply
            </DropdownMenuItem>
            <DropdownMenuItem
              tooltip={canTriggerWithCustomConfig.message}
              isDisabled={
                loading ||
                (!!canTriggerWithCustomConfig.message && !canTriggerWithCustomConfig.result)
              }
              isTooltipActive={!!canTriggerWithCustomConfig.message}
              tooltipPlacement="left"
              onAction={openCustomTriggerConfigDrawer}
            >
              Trigger with custom runtime config
            </DropdownMenuItem>
          </SplitControlDropdown>
        </SplitControlWrapper>
      ) : (
        <Tooltip
          active={!!canTrigger.message}
          on={(tooltipProps) => (
            <Button
              {...tooltipProps}
              disabled={loading || (!!canTrigger.message && !canTrigger.result)}
              loading={loading}
              variant={variant}
              id="trigger-button"
              size={size}
              onPress={() => onClick()}
            >
              {text}
            </Button>
          )}
          placement="top"
        >
          {canTrigger.message}
        </Tooltip>
      )}
    </>
  );
};

export default TriggerButton;
